/**
 * 매칭 셀 navigation 팝오버 — 현재 포커싱된 IXD 셀 옆에 anchor 시킨다.
 *
 * - virtualRef 는 `matchingCells[currentIndex]` 를 참조 — currentIndex 가 바뀌면
 *   ref 객체 자체가 새로 생성되어 Radix 가 재측정한다.
 * - 항목 label 은 셀의 `data-line-item-label` 속성에서 읽는다. 없으면
 *   matchNavigation.value (검색된 값) 로 fallback.
 * - 클릭 시 `onNavigateToIndex` 로 다른 매칭 셀로 이동.
 *
 * 라이브 비교 뷰(compare-content-view)와 다운로드 standalone 뷰어가 공유한다.
 */

import { useMemo } from "react";
import * as Popover from "@radix-ui/react-popover";

import type { MatchNavigation } from "@/app/ixd-dsd-compare/types";
import MatchNavigationFloatingPanel, {
  type MatchNavigationItem,
} from "./match-navigation-floating-panel";
import { formatSignedDiff } from "@/utils/ixd-dsd-compare/rounded-diff-label";

interface MatchNavigationPopoverProps {
  matchNavigation: MatchNavigation;
  onNavigateToIndex: (index: number) => void;
  onClose: () => void;
}

export const MatchNavigationPopover = ({
  matchNavigation,
  onNavigateToIndex,
  onClose,
}: MatchNavigationPopoverProps) => {
  const { matchingCells, currentIndex, value, differenceByCellId } =
    matchNavigation;
  const anchorCell = matchingCells[currentIndex] ?? null;

  // currentIndex 가 바뀔 때마다 새 ref 객체를 만들어 Radix Popover 가 재측정하게 한다.
  const anchorRef = useMemo<{ current: HTMLElement | null }>(
    () => ({ current: anchorCell }),
    [anchorCell],
  );

  const items = useMemo<MatchNavigationItem[]>(
    () =>
      matchingCells.map((cell, idx) => {
        const label =
          cell.getAttribute("data-line-item-label")?.trim() || value || "";
        const cellId = cell.getAttribute("data-cell-id");
        const diffLabel = formatSignedDiff(
          cellId ? differenceByCellId?.get(cellId) : undefined,
        );
        return {
          label,
          badge: diffLabel ? `단수차 ${diffLabel}` : undefined,
          onClick: () => onNavigateToIndex(idx),
        };
      }),
    [matchingCells, onNavigateToIndex, value, differenceByCellId],
  );

  return (
    <Popover.Root open onOpenChange={(open) => !open && onClose()}>
      <Popover.Anchor virtualRef={anchorRef} />
      <Popover.Portal>
        <Popover.Content
          side="right"
          align="center"
          sideOffset={2}
          collisionPadding={16}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          // anchor 셀을 다시 클릭하면 outside 로 잡혀 panel 이 닫히는 문제 방지.
          onPointerDownOutside={(event) => {
            const target = event.target as Node | null;
            if (target && anchorCell?.contains(target)) {
              event.preventDefault();
            }
          }}
          className="z-50"
        >
          <MatchNavigationFloatingPanel
            items={items}
            activeIndex={currentIndex}
            onClose={onClose}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
